import { OrderDirectionEnum, toast } from "shared-components";
import { create } from "zustand";

import { getRootThemesList, getSubthemesList, getSubthemesListFromRoot } from "@/api/theme";
import type { RootTheme, SubTheme } from "@/api/theme/models";
import { LICENSED_THEME_NAME } from "@/constants/theme";

export interface CommonTheme extends Required<RootTheme> {
  subThemes: RootTheme[];
}

interface ThemesStore {
  commonThemes: CommonTheme[];
  licensedThemes: Required<SubTheme>[];
  licensedRootTheme: RootTheme | null;
  featuredTheme: SubTheme | null;
  hasLoadedRootThemes: boolean;
  isLoadingRootThemes: boolean;
  isLoadingSubThemes: boolean;
  isLoadingLicensedThemes: boolean;
  isLoadingFeaturedTheme: boolean;
  loadRootThemes: () => void;
  loadSubThemes: (themeId: string) => void;
  loadLicensedThemes: () => void;
  loadFeaturedTheme: () => void;
}

const initialState = {
  commonThemes: [],
  licensedThemes: [],
  licensedRootTheme: null,
  featuredTheme: null,
  hasLoadedRootThemes: false,
  isLoadingRootThemes: false,
  isLoadingSubThemes: false,
  isLoadingLicensedThemes: false,
  isLoadingFeaturedTheme: false,
};

const fetchDataAndSaveToStore = <T>({
  fetchData,
  handleData,
  setLoading,
}: {
  fetchData: () => Promise<{ data?: T; error?: { message?: string } }>;
  handleData: (data: T) => void;
  setLoading: (isLoading: boolean) => void;
}) => {
  setLoading(true);

  fetchData()
    .then(({ data, error }) => {
      if (data) {
        handleData(data);
        return;
      }

      if (error?.message) {
        toast.error(error.message);
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      setLoading(false);
    });
};

export const useThemesStore = create<ThemesStore>((set, get) => ({
  ...initialState,
  loadRootThemes: () => {
    if (get().commonThemes.length && get().licensedRootTheme) {
      set({ hasLoadedRootThemes: true });
      return;
    }

    fetchDataAndSaveToStore<RootTheme[]>({
      setLoading: (isLoadingRootThemes) => set({ isLoadingRootThemes }),
      fetchData: () => getRootThemesList(null),
      handleData: (data) => {
        const commonThemes = data
          .filter((theme) => theme?.id && theme.name !== LICENSED_THEME_NAME)
          .map((theme) => ({
            ...theme,
            imageUrl: `/images/home/${theme.imageUrl}.svg`,
            subThemes: [],
          }));
        const licensedRootTheme = data.find((theme) => theme?.name === LICENSED_THEME_NAME) || null;

        set({
          commonThemes,
          licensedRootTheme,
          hasLoadedRootThemes: true,
        });
      },
    });
  },
  loadSubThemes: (themeId) => {
    const rootTheme = get().commonThemes.find(({ id }) => id === themeId);

    if (rootTheme && !rootTheme.subThemes.length) {
      fetchDataAndSaveToStore<SubTheme[]>({
        setLoading: (isLoadingSubThemes) => set({ isLoadingSubThemes }),
        fetchData: () =>
          getSubthemesListFromRoot({
            pagination: { page: 1, total: 100 },
            sort: [{ field: "name", order: OrderDirectionEnum.ASC }],
            id: themeId,
          }),
        handleData: (data) => {
          set({
            commonThemes: get().commonThemes.map((theme) => ({
              ...theme,
              subThemes:
                theme.id === themeId
                  ? data.map((subTheme) => ({
                      ...subTheme,
                      imageUrl: `/images/home/${subTheme.imageUrl}.svg`,
                    }))
                  : theme.subThemes,
            })),
          });
        },
      });
    }
  },
  loadLicensedThemes: () => {
    if (get().licensedRootTheme && !get().licensedThemes.length) {
      fetchDataAndSaveToStore<SubTheme[]>({
        setLoading: (isLoadingLicensedThemes) => set({ isLoadingLicensedThemes }),
        fetchData: () =>
          getSubthemesListFromRoot({
            pagination: { page: 1, total: 100 },
            sort: [{ field: "name", order: OrderDirectionEnum.ASC }],
            id: get().licensedRootTheme?.id || "",
          }),
        handleData: (data) => {
          set({
            licensedThemes: data.map((theme) => ({
              ...theme,
              imageUrl: `/images/home/${theme.imageUrl}.svg`,
            })),
          });
        },
      });
    }
  },
  loadFeaturedTheme: () => {
    if (!get().featuredTheme) {
      fetchDataAndSaveToStore<SubTheme[]>({
        setLoading: (isLoadingFeaturedTheme) => set({ isLoadingFeaturedTheme }),
        fetchData: () =>
          getSubthemesList({
            pagination: { page: 1, total: 1 },
            filters: [{ field: "highlight", value: true }],
          }),
        handleData: (data) => {
          if (data.length) {
            set({ featuredTheme: data[0] });
          }
        },
      });
    }
  },
}));
